import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ProviderId } from '@trivio/contracts';

/**
 * Onboarding progress, persisted to `localStorage` (TTP-243 acceptance
 * criterion: "estado do onboarding persistido — reload retoma a etapa
 * correta; quem já completou não vê onboarding de novo"). Everything here
 * is non-sensitive ("em que etapa estou") — never the raw API keys (those
 * live only in ApiKeysContext, in memory) and never the provider credential
 * the lojista pastes in Tela 3 (that never leaves the PUT call, see
 * ProvidersStep.tsx).
 *
 * A single shared React Context (like AuthContext/ApiKeysContext), NOT a
 * plain hook called independently in every consumer — confirmed empirically
 * in this session to matter, not just a style preference: React Router's
 * `Routes` reuses the SAME `OnboardingStepGuard` component instance across
 * sibling `/onboarding/*` route matches (same component type at the same
 * tree position — ordinary React reconciliation, nothing router-specific).
 * With one independent `useOnboardingState(uid)` call per consumer, the
 * guard's own hook instance and a step component's own hook instance are
 * two SEPARATE pieces of in-memory state that happen to share a
 * `localStorage` key — a step's `update()` (e.g. ProvidersStep navigating
 * to Tela 4) would persist correctly, but the guard's already-mounted,
 * reused instance never re-read storage, so it evaluated the OLD step and
 * bounced the user straight back. A single Provider makes that structurally
 * impossible: there is only ever one `state`/`update` for every consumer to
 * share.
 */

export type OnboardingStepName = 'cnpj' | 'providers' | 'demo' | 'api-key' | 'completed';

export interface OnboardingState {
  step: OnboardingStepName;
  orgId: string | null;
  /** Providers with an `active` connection as of Tela 3 — drives Tela 4/5's live-vs-sandbox branch. */
  connectedProviders: ProviderId[];
  /** Origin postal code, once known (e.g. typed into a provider's onboarding elsewhere) — prefills Tela 4's demo quote. Null until then. */
  fromPostalCode: string | null;
}

const DEFAULT_STATE: OnboardingState = {
  step: 'cnpj',
  orgId: null,
  connectedProviders: [],
  fromPostalCode: null,
};

function storageKey(uid: string): string {
  return `trivio:onboarding:${uid}`;
}

function readState(uid: string): OnboardingState {
  try {
    const raw = window.localStorage.getItem(storageKey(uid));
    if (!raw) {
      return DEFAULT_STATE;
    }
    const parsed = JSON.parse(raw) as Partial<OnboardingState>;
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return DEFAULT_STATE;
  }
}

function writeState(uid: string, state: OnboardingState): void {
  try {
    window.localStorage.setItem(storageKey(uid), JSON.stringify(state));
  } catch {
    // Storage unavailable (private-browsing quota, disabled cookies/storage)
    // — the current page load still works from in-memory React state, it
    // just won't survive a reload. Non-fatal, never blocks the flow.
  }
}

export interface UseOnboardingState {
  state: OnboardingState;
  update: (patch: Partial<OnboardingState>) => void;
}

const OnboardingStateContext = createContext<UseOnboardingState | null>(null);

export interface OnboardingStateProviderProps {
  /** `undefined` while auth is still resolving, `null` if signed out — both fall back to `DEFAULT_STATE` (nothing persisted, since there's no user to key it by). Takes `uid` as a prop (rather than calling `useAuth()` itself) so this provider is unit-testable without a Firebase mock — see `src/app/providers.tsx` for how the real app wires `uid` in from `AuthContext`. */
  uid: string | null | undefined;
  children: ReactNode;
}

export function OnboardingStateProvider({ uid, children }: OnboardingStateProviderProps) {
  const [state, setState] = useState<OnboardingState>(() => (uid ? readState(uid) : DEFAULT_STATE));
  const lastSyncedUidRef = useRef(uid);

  // Re-sync from storage the moment `uid` changes (e.g. auth resolves after
  // this provider's first render, going from `undefined` to a real uid) —
  // deliberately done DURING RENDER via this "adjust state while rendering"
  // pattern (see https://react.dev/reference/react/useState#storing-information-from-previous-renders),
  // NOT in a `useEffect`, which would leave a one-render window where
  // `state` still reflected the previous uid (or DEFAULT_STATE).
  if (uid !== lastSyncedUidRef.current) {
    lastSyncedUidRef.current = uid;
    setState(uid ? readState(uid) : DEFAULT_STATE);
  }

  // Writes to localStorage SYNCHRONOUSLY, in the caller's own call stack —
  // deliberately not inside a `setState(prev => ...)` updater (React
  // doesn't guarantee an updater runs before the next line of caller code).
  // A step component's handler pattern is "persist the next step, then
  // `navigate()`" — the next route's guard must see the update's effects
  // immediately, not on some later, unspecified render.
  const update = useCallback(
    (patch: Partial<OnboardingState>) => {
      setState((prev) => {
        const next = { ...prev, ...patch };
        if (uid) {
          writeState(uid, next);
        }
        return next;
      });
    },
    [uid],
  );

  const value = useMemo<UseOnboardingState>(() => ({ state, update }), [state, update]);

  return <OnboardingStateContext.Provider value={value}>{children}</OnboardingStateContext.Provider>;
}

export function useOnboardingState(): UseOnboardingState {
  const ctx = useContext(OnboardingStateContext);
  if (!ctx) {
    throw new Error('useOnboardingState must be used within an OnboardingStateProvider');
  }
  return ctx;
}
